import { DismissableLayer } from "@radix-ui/react-dismissable-layer";
import { Shapes } from "lucide-react";
import { useId, useMemo, useRef, useState } from "react";

import { Checkbox } from "@/components/ui/checkbox.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { useRoutines } from "@/hooks/useRoutines.ts";
import { useSkills } from "@/hooks/useSkills.ts";
import { areaKeyOf, customAreaNames, toggleRole } from "@/shared/areaRoleFilter.ts";
import {
  cleanUserArea,
  ROUTINE_AREAS,
  ROUTINE_ROLES,
  type RoutineRoleId,
  routineRoleIdSchema,
  USER_AREA_MAX_LENGTH,
} from "@/types/routineAreas.ts";

const ADD_AREA = "__add-area__";

/**
 * The areas the user added to their own routines and skills, from the two lists
 * the app already loads. The api keeps no list of its own: an area exists while
 * an item carries it.
 */
export const useUserCustomAreas = (): string[] => {
  const { routines } = useRoutines();
  const { skills } = useSkills();
  return useMemo(() => customAreaNames([...routines, ...skills]), [routines, skills]);
};

/**
 * The area menu of a user's own routine or skill: the built-in areas, the
 * user's own areas, and "Add your own area…", which opens a field for a new
 * name. `value` is what the item stores: a built-in area id, or a name.
 */
export const AreaField = ({
  value,
  onChange,
  customAreas,
  statsigPrefix,
}: {
  value: string | undefined;
  onChange: (area: string) => void;
  customAreas: readonly string[];
  statsigPrefix: string;
}) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const inputId = useId();
  // Set while the menu closes on "Add your own area…": the menu would hand
  // focus back to its trigger, which would blur the new field and close it.
  const openingAdd = useRef(false);

  // One option per area key, so the stored spelling of a custom area selects
  // its entry whatever case the list shows it in. The current value is always
  // offered, even before the routine and skill lists have loaded.
  const selectedKey = areaKeyOf(value);
  const custom = useMemo(() => {
    const names = new Map(customAreas.map((name) => [areaKeyOf(name), name]));
    if (selectedKey?.startsWith("custom:") && value && !names.has(selectedKey)) {
      names.set(selectedKey, value.trim());
    }
    return [...names].flatMap(([key, name]) => (key ? [{ key, name }] : []));
  }, [customAreas, selectedKey, value]);

  // The name as the api will store it; one it would empty keeps the area as it was.
  const commitDraft = () => {
    const name = cleanUserArea(draft);
    setAdding(false);
    setDraft("");
    if (name) onChange(name);
  };
  const cancelDraft = () => {
    setAdding(false);
    setDraft("");
  };

  return (
    <div className="flex flex-col gap-2">
      <Select
        value={selectedKey ?? ""}
        onValueChange={(key) => {
          if (key === ADD_AREA) {
            openingAdd.current = true;
            setAdding(true);
            return;
          }
          const builtIn = ROUTINE_AREAS.find((area) => area.id === key);
          const chosen = builtIn?.id ?? custom.find((area) => area.key === key)?.name;
          if (chosen) onChange(chosen);
        }}
      >
        <SelectTrigger
          aria-label="Area"
          className="h-8 w-auto min-w-48 gap-1.5 rounded-md text-sm"
          data-statsig-name={`${statsigPrefix}-area`}
        >
          <SelectValue placeholder="Choose an area" />
        </SelectTrigger>
        <SelectContent
          onCloseAutoFocus={(event) => {
            if (!openingAdd.current) return;
            openingAdd.current = false;
            event.preventDefault();
            document.getElementById(inputId)?.focus();
          }}
        >
          <SelectGroup>
            {ROUTINE_AREAS.map((area) => (
              <SelectItem key={area.id} value={area.id}>
                {area.name}
              </SelectItem>
            ))}
          </SelectGroup>
          {custom.length > 0 ? (
            <>
              <SelectSeparator />
              <SelectGroup>
                <SelectLabel>Your areas</SelectLabel>
                {custom.map((area) => (
                  <SelectItem key={area.key} value={area.key}>
                    {area.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            </>
          ) : null}
          <SelectSeparator />
          <SelectItem value={ADD_AREA} data-statsig-name={`${statsigPrefix}-area-add`}>
            Add your own area…
          </SelectItem>
        </SelectContent>
      </Select>
      {adding ? (
        // A layer of its own, so Escape cancels the new name and stops here:
        // inside a dialog (the canvas routine card) the dialog would otherwise
        // take the Escape and close, with every unsaved edit in it.
        <DismissableLayer onEscapeKeyDown={cancelDraft}>
          <Label htmlFor={inputId} className="sr-only">
            New area name
          </Label>
          <Input
            id={inputId}
            autoFocus
            value={draft}
            maxLength={USER_AREA_MAX_LENGTH}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={commitDraft}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                // Enter adds the area; it must not also submit the form around it.
                event.preventDefault();
                commitDraft();
              }
            }}
            placeholder="For example, Catering"
            className="h-8 max-w-64 text-sm"
          />
        </DismissableLayer>
      ) : null}
    </div>
  );
};

/**
 * The roles a user's own routine or skill suits, one checkbox each. None
 * checked means every role, as the api reads an empty list.
 */
export const RolesField = ({
  value,
  onChange,
  statsigPrefix,
}: {
  value: readonly string[];
  onChange: (roles: RoutineRoleId[]) => void;
  statsigPrefix: string;
}) => {
  const idPrefix = useId();
  const roles = knownRoles(value);

  return (
    <fieldset className="flex flex-col gap-2 border-none p-0">
      <legend className="sr-only">Roles</legend>
      <p className="text-xs text-muted-foreground">
        Who it suits. Leave every box empty if it suits every role.
      </p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        {ROUTINE_ROLES.map((role) => {
          const id = `${idPrefix}-${role.id}`;
          return (
            <div key={role.id} className="flex items-center gap-2">
              <Checkbox
                id={id}
                checked={roles.includes(role.id)}
                onCheckedChange={() => onChange(toggleRole(roles, role.id))}
                data-statsig-name={`${statsigPrefix}-role-${role.id}`}
              />
              <Label htmlFor={id} className="cursor-pointer text-sm font-normal">
                {role.name}
              </Label>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
};

/**
 * The area and roles section of a user's own routine or skill, controlled by
 * its form. It reads the user's own areas itself, so a page loads the skill and
 * routine lists for it only while the section shows.
 */
export const AreaRoleSection = ({
  area,
  onAreaChange,
  roles,
  onRolesChange,
  statsigPrefix,
}: {
  area: string | undefined;
  onAreaChange: (area: string) => void;
  roles: readonly string[];
  onRolesChange: (roles: RoutineRoleId[]) => void;
  statsigPrefix: string;
}) => {
  const customAreas = useUserCustomAreas();
  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <Shapes className="size-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Area and roles
        </span>
      </div>
      <div className="flex flex-col gap-4">
        <AreaField
          value={area}
          onChange={onAreaChange}
          customAreas={customAreas}
          statsigPrefix={statsigPrefix}
        />
        <RolesField value={roles} onChange={onRolesChange} statsigPrefix={statsigPrefix} />
      </div>
    </div>
  );
};

/**
 * The roles in `values` that the role list still has. A stored role that has
 * left the list is dropped, so a form neither shows it nor sends it back, which
 * the api would refuse.
 */
export const knownRoles = (values: readonly string[] | undefined): RoutineRoleId[] =>
  (values ?? []).flatMap((role) => {
    const parsed = routineRoleIdSchema.safeParse(role);
    return parsed.success ? [parsed.data] : [];
  });

/**
 * The area and roles a save sends: only those the user changed from what is
 * stored. A stored role that has left the list would fail the request, so an
 * edit to some other field must not send the roles back; and the form offers
 * no way to clear an area, so an unset one is never sent.
 */
export const areaRolePatch = (
  stored: { area?: string; roles?: readonly string[] },
  edited: { area: string | undefined; roles: RoutineRoleId[] },
): { area?: string; roles?: RoutineRoleId[] } => ({
  // By area key: "catering" typed over "Catering", or "Money" over `money`, is
  // the same area and no change.
  ...(edited.area !== undefined && areaKeyOf(edited.area) !== areaKeyOf(stored.area)
    ? { area: edited.area }
    : {}),
  ...(sameRoles(edited.roles, knownRoles(stored.roles)) ? {} : { roles: edited.roles }),
});

/**
 * True when two role lists hold the same roles, in any order: a stored list
 * need not be in the role list's order, and a form's list always is.
 */
export const sameRoles = (a: readonly string[], b: readonly string[]): boolean =>
  new Set(a).size === new Set(b).size && a.every((role) => b.includes(role));
